import type { ICampaignThreadMessage } from '../redux/buzzbridge/campaignThreadMessage.types';

function unwrapData(raw: unknown): Record<string, unknown> {
    if (raw == null || typeof raw !== 'object') return {};
    const obj = raw as Record<string, unknown>;
    if (obj.data != null && typeof obj.data === 'object' && !Array.isArray(obj.data)) {
        return obj.data as Record<string, unknown>;
    }
    return obj;
}

function pickString(...values: unknown[]): string | undefined {
    for (const value of values) {
        if (value == null) continue;
        const str = String(value).trim();
        if (str) return str;
    }
    return undefined;
}

function pickBool(...values: unknown[]): boolean | undefined {
    for (const value of values) {
        if (typeof value === 'boolean') return value;
        if (value === 'true') return true;
        if (value === 'false') return false;
    }
    return undefined;
}

export function normalizeCampaignThreadParticipantId(raw: Record<string, unknown>): string | undefined {
    return pickString(raw.participantId, raw.participant_id, raw.participant);
}

export function normalizeCampaignThreadMessagePayload(raw: unknown): {
    participantId: string;
    message: ICampaignThreadMessage;
} | null {
    const body = unwrapData(raw);
    const participantId = normalizeCampaignThreadParticipantId(body);
    const message = (body.message ?? body.newMessage) as ICampaignThreadMessage | undefined;
    if (!message || typeof message !== 'object') return null;
    const resolvedParticipantId = participantId ?? pickString((message as ICampaignThreadMessage).participant);
    if (!resolvedParticipantId) return null;
    return { participantId: resolvedParticipantId, message };
}

export function normalizeCampaignThreadDeletedPayload(raw: unknown): {
    participantId: string;
    messageId: string;
} | null {
    const body = unwrapData(raw);
    const participantId = normalizeCampaignThreadParticipantId(body);
    const messageId = pickString(body.messageId, body.message_id, body._id);
    if (!participantId || !messageId) return null;
    return { participantId, messageId };
}

export function normalizeCampaignThreadAttachmentProcessingPayload(raw: unknown): {
    participantId: string;
    messageId: string;
    attachmentId: string;
    processing_progress?: number;
    processing_complete?: boolean;
    processing_error?: string;
} | null {
    const body = unwrapData(raw);
    const participantId = normalizeCampaignThreadParticipantId(body);
    const messageId = pickString(body.messageId, body.message_id);
    const attachmentId = pickString(body.attachmentId, body.attachment_id);
    if (!participantId || !messageId || !attachmentId) return null;
    return {
        participantId,
        messageId,
        attachmentId,
        processing_progress: typeof body.processing_progress === 'number' ? body.processing_progress : undefined,
        processing_complete: pickBool(body.processing_complete),
        processing_error: pickString(body.processing_error),
    };
}

export function normalizeCampaignThreadUnreadPayload(raw: unknown): {
    participantId: string;
    unread_count: number;
    thread_role: 'host' | 'influencer';
} | null {
    const body = unwrapData(raw);
    const participantId = normalizeCampaignThreadParticipantId(body);
    const unread_count = body.unread_count ?? body.unreadCount;
    const thread_role = body.thread_role ?? body.threadRole;
    if (!participantId || typeof unread_count !== 'number') return null;
    if (thread_role !== 'host' && thread_role !== 'influencer') return null;
    return { participantId, unread_count, thread_role };
}

export function normalizeCampaignThreadTypingPayload(raw: unknown): {
    participantId: string;
    userId: string;
    typing: boolean;
    displayName?: string;
} | null {
    const body = unwrapData(raw);
    const participantId = normalizeCampaignThreadParticipantId(body);
    const userObj = body.user != null && typeof body.user === 'object' ? (body.user as Record<string, unknown>) : null;
    const senderObj =
        body.sender != null && typeof body.sender === 'object' ? (body.sender as Record<string, unknown>) : null;
    const typingObj =
        body.typing != null && typeof body.typing === 'object' ? (body.typing as Record<string, unknown>) : null;
    const userId = pickString(
        body.userId,
        body.user_id,
        userObj?._id,
        userObj?.id,
        senderObj?._id,
        senderObj?.id,
        typingObj?.userId,
        typingObj?.user_id,
        typeof body.user === 'string' ? body.user : undefined,
        body.influencerId,
        body.influencer_id,
    );
    const typing =
        pickBool(body.typing, body.isTyping, typingObj?.typing, typingObj?.isTyping) ??
        (body.status === 'typing' || body.status === 'yes'
            ? true
            : body.status === 'stopped' || body.status === 'no'
            ? false
            : undefined);
    if (!participantId || typing === undefined) return null;
    const displayName = pickString(
        body.displayName,
        body.display_name,
        userObj?.display_name,
        userObj?.displayName,
        userObj?.firstName,
        userObj?.lastName,
        userObj?.userName,
        senderObj?.display_name,
        senderObj?.displayName,
        senderObj?.firstName,
        senderObj?.userName,
        typingObj?.displayName,
        typingObj?.display_name,
    );
    return { participantId, userId: userId ?? '', typing, displayName };
}

export function normalizeCampaignThreadErrorPayload(raw: unknown): { code?: string; message: string } {
    const body = unwrapData(raw);
    const message = pickString(body.message, body.error) ?? 'Campaign thread error';
    const code = pickString(body.code, (body.details as Record<string, unknown> | undefined)?.code);
    return { code, message };
}

/** Event names the backend may emit that we normalize onto canonical handlers. */
export const CAMPAIGN_THREAD_SOCKET_ALIASES = {
    messageNew: ['campaignThread:messageNew', 'campaignThread:newMessage', 'campaign_thread:messageNew'],
    messageUpdated: ['campaignThread:messageUpdated', 'campaignThread:messageEdited', 'campaign_thread:messageUpdated'],
    messageDeleted: ['campaignThread:messageDeleted', 'campaign_thread:messageDeleted'],
    typing: ['campaignThread:typing', 'campaign_thread:typing'],
    attachmentProcessing: ['campaignThread:attachmentProcessing', 'campaign_thread:attachmentProcessing'],
    unreadUpdated: ['campaignThread:unreadUpdated', 'campaign_thread:unreadUpdated'],
    error: ['campaignThread:error', 'campaign_thread:error'],
} as const;

export function registerCampaignThreadSocketAliases(
    socket: { on: (event: string, handler: (payload: unknown) => void) => void },
    canonicalEvent: string,
    aliasEvents: readonly string[],
    handler: (payload: unknown) => void,
) {
    const events = new Set([canonicalEvent, ...aliasEvents]);
    events.forEach(event => socket.on(event, handler));
}

/** Documented but not handled on web client (informational). */
export const CAMPAIGN_THREAD_SOCKET_CLIENT_EMITS_ONLY = [
    'campaignThread:send', // send uses REST; socket send optional
    'campaignThread:markRead', // mark-read uses REST
] as const;

export const CAMPAIGN_THREAD_SOCKET_NOT_WIRED_ON_CLIENT = [...CAMPAIGN_THREAD_SOCKET_CLIENT_EMITS_ONLY] as const;
